import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, X, ChevronRight, AlertTriangle, CheckCircle2, FileText, ArrowLeft, Building2, Users, TrendingUp, Database } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const btn = "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground";
const primary = "inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white disabled:opacity-60";
const outline = "inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold text-foreground hover:bg-muted";

const KIND_LABEL = { companies: "Companies", contacts: "Contacts", deals: "Deals", relational: "CRM Data (Company + Contact + Deal)" };
const REQUIRED_FIELD = { companies: "name", contacts: "name", deals: "title", relational: null };

// Human-readable labels for canonical fields in relational mode
const RELATIONAL_LABEL = {
  "company.name": "Company Name",
  "company.company_type": "Company Type",
  "company.industry": "Industry",
  "company.website": "Website",
  "company.email": "Company Email",
  "company.phone": "Main Phone",
  "company.linkedin": "LinkedIn URL",
  "company.address": "Address",
  "company.city": "City",
  "company.state": "State / Province",
  "company.postal_code": "Postal Code",
  "company.country": "Country",
  "company.lead_source": "Lead Source",
  "company.status": "Status",
  "company.notes": "Notes",
  "contact.full_name": "Full Name",
  "contact.first_name": "First Name",
  "contact.last_name": "Last Name",
  "contact.job_title": "Job Title",
  "contact.email": "Email",
  "contact.secondary_email": "Secondary Email",
  "contact.phone": "Phone",
  "contact.mobile": "Mobile / WhatsApp",
  "contact.linkedin": "LinkedIn URL",
  "contact.website": "Website",
  "contact.city": "City",
  "contact.state": "State / Province",
  "contact.postal_code": "Postal Code",
  "contact.country": "Country",
  "contact.lead_source": "Lead Source",
  "contact.status": "Status",
  "contact.notes": "Notes",
  "deal.title": "Deal Name",
  "deal.stage": "Deal Stage",
  "deal.value": "Deal Amount",
  "deal.currency": "Currency",
  "deal.close_date": "Expected Close Date",
  "deal.next_follow_up": "Next Follow-up",
  "deal.probability": "Probability (%)",
  "deal.source": "Deal Source",
  "deal.product_service": "Product / Service",
  "deal.notes": "Notes",
};

function FieldPicker({ csvHeader, canonical, chosen, onChange, isRequired, isConflict, relational }) {
  const grouped = relational
    ? canonical.reduce((acc, f) => {
        const entity = f.split(".")[0];
        (acc[entity] = acc[entity] || []).push(f);
        return acc;
      }, {})
    : null;

  const groupLabel = { company: "Company", contact: "Contact", deal: "Deal" };

  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`csv-map-row-${csvHeader}`}>
      <span className="w-40 truncate rounded bg-muted px-2 py-1 text-xs font-medium">{csvHeader}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground" />
      <select
        value={chosen ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={`csv-map-select-${csvHeader}`}
        className={`w-56 rounded-md border ${isConflict ? "border-brand-red" : "border-border"} bg-background px-2 py-1 text-xs outline-none focus:border-primary`}
      >
        <option value="">— skip this column —</option>
        {relational
          ? ["company", "contact", "deal"].map((entity) => (
              <optgroup key={entity} label={groupLabel[entity]}>
                {(grouped[entity] || []).map((f) => (
                  <option key={f} value={f}>{RELATIONAL_LABEL[f] || f}</option>
                ))}
              </optgroup>
            ))
          : canonical.map((f) => (
              <option key={f} value={f}>{f}{f === isRequired ? " (required)" : ""}</option>
            ))}
      </select>
    </div>
  );
}

export default function CsvImportWizard({ kind, open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload -> preview -> map -> validate -> confirm -> done
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null); // {headers, rows, auto_map, canonical_fields}
  const [mapping, setMapping] = useState({}); // csv_header -> canonical
  const [validation, setValidation] = useState(null); // per-kind flow only
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const [companyConflict, setCompanyConflict] = useState("fill_blanks");
  const [contactConflict, setContactConflict] = useState("fill_blanks");
  const [confirmResult, setConfirmResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const isRelational = kind === "relational";

  const reset = () => {
    setStep("upload"); setCsvText(""); setFileName(""); setPreview(null);
    setMapping({}); setValidation(null); setDuplicateStrategy("skip");
    setCompanyConflict("fill_blanks"); setContactConflict("fill_blanks");
    setConfirmResult(null); setBusy(false);
  };
  const close = () => { onOpenChange(false); setTimeout(reset, 400); };

  const readFileAsCsv = async (file) => {
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      // Convert to base64 and let backend do the parsing
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // btoa needs a string
      let bin = "";
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = btoa(bin);
      const { data } = await api.post("/csv/xlsx-to-csv", {
        xlsx_base64: b64, kind: isRelational ? "relational" : kind,
      });
      return data.csv || "";
    }
    return await file.text();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileName(file.name);
    try {
      setBusy(true);
      const text = await readFileAsCsv(file);
      setCsvText(text);
      const endpoint = isRelational ? "/csv/relational/preview" : "/csv/preview";
      const body = isRelational ? { csv: text } : { csv: text, kind };
      const { data } = await api.post(endpoint, body);
      setPreview(data);
      setMapping(data.auto_map || {});
      setStep("preview");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse file.");
    } finally { setBusy(false); }
  };

  const validate = async () => {
    if (isRelational) {
      // Skip validate step for relational — jump straight from map to confirm
      const mapped = Object.values(mapping || {}).filter(Boolean);
      if (!mapped.includes("company.name") && !mapped.includes("contact.full_name") &&
          !mapped.includes("contact.first_name") && !mapped.includes("contact.email") &&
          !mapped.includes("deal.title")) {
        toast.error("Map at least one of: Company Name, Contact Full Name / Email, or Deal Name.");
        return;
      }
      setStep("validate");
      return;
    }
    const required = REQUIRED_FIELD[kind];
    const mapped = Object.values(mapping);
    if (required && !mapped.includes(required)) {
      toast.error(`Please map a CSV column to '${required}' — this field is required.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/csv/validate", { csv: csvText, kind, mapping });
      setValidation(data);
      setStep("validate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Validation failed.");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      if (isRelational) {
        const { data } = await api.post("/csv/relational/confirm", {
          csv: csvText, mapping,
          company_conflict_strategy: companyConflict,
          contact_conflict_strategy: contactConflict,
        });
        setConfirmResult({ ...data, relational: true });
      } else {
        const { data } = await api.post("/csv/confirm", {
          csv: csvText, kind, mapping, duplicate_strategy: duplicateStrategy,
        });
        setConfirmResult(data);
      }
      setStep("done");
      onImported && onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const requiredField = REQUIRED_FIELD[kind];
  const mappedFields = Object.values(mapping || {}).filter(Boolean);
  const hasDuplicateAssignment = new Set(mappedFields).size !== mappedFields.length;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="csv-wizard">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-card p-5 sm:rounded-3xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow text-brand-teal">CSV Import · {KIND_LABEL[kind]}</p>
            <h2 className="mt-1 font-display text-xl font-semibold tracking-tight">
              {step === "upload" && "Upload your CSV"}
              {step === "preview" && "Review the parsed rows"}
              {step === "validate" && "Validate & resolve duplicates"}
              {step === "confirm" && "Confirm import"}
              {step === "done" && "Import complete"}
            </h2>
          </div>
          <button onClick={close} className="text-muted-foreground hover:text-foreground" data-testid="csv-wizard-close"><X className="h-5 w-5" /></button>
        </div>

        <Stepper current={step} />

        {step === "upload" && (
          <div className="mt-6">
            <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} className="hidden" data-testid="csv-wizard-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border p-10 text-center hover:border-brand-teal" data-testid="csv-wizard-file-btn">
              <Upload className="h-8 w-8 text-brand-teal" />
              <div>
                <p className="text-sm font-semibold">{busy ? "Reading file…" : "Select a CSV or XLSX file"}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isRelational
                    ? "One row per lead — we'll create the Company, Contact and Deal and link them together."
                    : "We'll parse it, let you review the mapping, and only import once you confirm."}
                </p>
              </div>
            </button>
            {isRelational && (
              <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                <div className="rounded-xl border border-border bg-background p-2">
                  <p className="mb-1 flex items-center gap-1 font-semibold text-foreground"><Building2 className="h-3 w-3" /> Company</p>
                  Name, Website, Industry, City, State, Country…
                </div>
                <div className="rounded-xl border border-border bg-background p-2">
                  <p className="mb-1 flex items-center gap-1 font-semibold text-foreground"><Users className="h-3 w-3" /> Contact</p>
                  Full Name, Title, Email, Phone, City, Country…
                </div>
                <div className="rounded-xl border border-border bg-background p-2">
                  <p className="mb-1 flex items-center gap-1 font-semibold text-foreground"><TrendingUp className="h-3 w-3" /> Deal</p>
                  Name, Amount, Stage, Close Date, Notes…
                </div>
              </div>
            )}
            {!isRelational && (
              <p className="mt-4 text-xs text-muted-foreground">
                Tip: Use <code className="rounded bg-muted px-1 py-0.5">Export</code> first if you want to see the exact CSV shape we understand best.
              </p>
            )}
          </div>
        )}

        {step === "preview" && preview && (
          <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-border bg-background p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">File</p>
              <div className="mt-1 flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-brand-teal" /> {fileName || "uploaded.csv"} · ~{preview.total_estimated_rows} rows</div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-left text-xs" data-testid="csv-preview-table">
                <thead className="bg-muted/60"><tr>{preview.headers.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {preview.rows.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {preview.headers.map((h) => <td key={h} className="px-3 py-1.5 text-muted-foreground">{String(row[h] ?? "").slice(0, 60)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.rows.length > 5 && <p className="border-t border-border bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground">Showing first 5 of {preview.rows.length} rows in preview.</p>}
            </div>
            <div className="flex justify-between">
              <button className={outline} onClick={reset} data-testid="csv-wizard-restart"><ArrowLeft className="h-3 w-3" /> Choose another file</button>
              <button className={primary} onClick={() => setStep("map")} data-testid="csv-wizard-to-map">Next: map columns <ChevronRight className="h-3 w-3" /></button>
            </div>
          </div>
        )}

        {step === "map" && preview && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              {isRelational
                ? "Choose which entity each column belongs to (Company / Contact / Deal). We auto-mapped what we recognised — adjust anything that looks wrong."
                : "We auto-matched columns to workspace fields. Adjust any that look wrong, or leave uncertain ones as \"skip\"."}
              {!isRelational && (<span className="ml-1 font-semibold text-foreground">Required: {requiredField}</span>)}
            </p>
            <div className="rounded-xl border border-border bg-background p-3">
              {preview.headers.map((h) => (
                <FieldPicker
                  key={h}
                  csvHeader={h}
                  canonical={preview.canonical_fields}
                  chosen={mapping[h] ?? null}
                  isRequired={requiredField}
                  isConflict={mapping[h] && mappedFields.filter((f) => f === mapping[h]).length > 1}
                  relational={isRelational}
                  onChange={(v) => setMapping((m) => ({ ...m, [h]: v }))}
                />
              ))}
            </div>
            {hasDuplicateAssignment && (
              <div className="flex items-start gap-2 rounded-xl bg-brand-red/10 p-3 text-xs text-brand-red" data-testid="csv-map-conflict">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                <span>Two columns are mapped to the same field. Please pick different fields or set one to "skip".</span>
              </div>
            )}
            <div className="flex justify-between">
              <button className={outline} onClick={() => setStep("preview")} data-testid="csv-wizard-back-map"><ArrowLeft className="h-3 w-3" /> Back</button>
              <button className={primary} onClick={validate} disabled={busy || hasDuplicateAssignment} data-testid="csv-wizard-validate">{busy ? "Preparing…" : (isRelational ? "Next: review" : "Next: validate")} <ChevronRight className="h-3 w-3" /></button>
            </div>
          </div>
        )}

        {step === "validate" && isRelational && (
          <div className="mt-4 space-y-3" data-testid="csv-relational-review">
            <p className="text-xs text-muted-foreground">
              Ready to import {preview?.total_estimated_rows || preview?.rows?.length || 0} row(s). Each row will create or reuse a Company, a Contact, and a Deal — all linked together.
            </p>
            <div className="rounded-xl border border-border bg-background p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">If a Company or Contact already exists…</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <ConflictPicker
                  label="Company conflicts"
                  value={companyConflict}
                  onChange={setCompanyConflict}
                  testid="csv-company-conflict"
                />
                <ConflictPicker
                  label="Contact conflicts"
                  value={contactConflict}
                  onChange={setContactConflict}
                  testid="csv-contact-conflict"
                />
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                <span className="font-semibold text-foreground">Fill blanks</span> only writes fields that are currently empty on the existing record.
                <span className="mx-1">·</span>
                <span className="font-semibold text-foreground">Keep existing</span> never overwrites data.
                <span className="mx-1">·</span>
                <span className="font-semibold text-foreground">Overwrite</span> replaces every provided field.
              </p>
            </div>
            <div className="flex justify-between">
              <button className={outline} onClick={() => setStep("map")} data-testid="csv-wizard-back-validate"><ArrowLeft className="h-3 w-3" /> Back to mapping</button>
              <button className={primary} onClick={confirmImport} disabled={busy} data-testid="csv-wizard-confirm">
                {busy ? "Importing…" : "Import CRM data"}
              </button>
            </div>
          </div>
        )}

        {step === "validate" && !isRelational && validation && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="Ready to import" value={validation.valid} tone="ok" testid="csv-stat-valid" />
              <StatCard label="Skipped" value={validation.skipped.length} tone="warn" testid="csv-stat-skipped" />
              <StatCard label="Duplicates" value={validation.duplicates.length} tone="info" testid="csv-stat-dupes" />
            </div>
            {validation.skipped.length > 0 && (
              <div className="rounded-xl border border-border p-3">
                <p className="mb-1 text-xs font-semibold">Skipped rows</p>
                <ul className="max-h-32 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                  {validation.skipped.slice(0, 20).map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
                </ul>
              </div>
            )}
            {validation.duplicates.length > 0 && (
              <div className="rounded-xl border border-border p-3" data-testid="csv-duplicate-review">
                <p className="mb-2 text-xs font-semibold">Existing records matched — how should we handle them?</p>
                <div className="flex flex-wrap gap-2">
                  {[["skip", "Skip duplicates"], ["update", "Update existing"], ["create", "Create as new"]].map(([k, l]) => (
                    <button key={k} onClick={() => setDuplicateStrategy(k)} data-testid={`csv-dup-strategy-${k}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${duplicateStrategy === k ? "border-brand-teal bg-brand-teal/10 text-brand-teal" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
                  ))}
                </div>
                <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-[11px] text-muted-foreground">
                  {validation.duplicates.slice(0, 20).map((d, i) => (
                    <li key={i}>Row {d.row}: matches existing "{d.existing?.name || d.existing?.title || d.existing?.email}"</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-between">
              <button className={outline} onClick={() => setStep("map")} data-testid="csv-wizard-back-validate"><ArrowLeft className="h-3 w-3" /> Back to mapping</button>
              <button className={primary} onClick={confirmImport} disabled={busy || validation.valid === 0} data-testid="csv-wizard-confirm">
                {busy ? "Importing…" : `Import ${validation.valid} ${KIND_LABEL[kind].toLowerCase()}`}
              </button>
            </div>
          </div>
        )}

        {step === "done" && confirmResult && (
          <div className="mt-6 text-center" data-testid="csv-wizard-done">
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-teal" />
            {confirmResult.relational ? (
              <>
                <h3 className="mt-3 font-display text-lg font-semibold tracking-tight">CRM data imported</h3>
                <div className="mx-auto mt-4 grid max-w-md grid-cols-3 gap-2 text-left">
                  <RelStat label="Companies" created={confirmResult.companies_created} reused={confirmResult.companies_reused} updated={confirmResult.companies_updated} testid="csv-done-companies" />
                  <RelStat label="Contacts" created={confirmResult.contacts_created} reused={confirmResult.contacts_reused} updated={confirmResult.contacts_updated} testid="csv-done-contacts" />
                  <RelStat label="Deals" created={confirmResult.deals_created} reused={0} updated={0} skipped={confirmResult.deals_skipped} testid="csv-done-deals" />
                </div>
                {confirmResult.conflicts && confirmResult.conflicts.length > 0 && (
                  <details className="mx-auto mt-4 max-w-md rounded-xl border border-border bg-background p-3 text-left text-[11px]">
                    <summary className="cursor-pointer font-semibold">{confirmResult.conflicts.length} field conflict(s) preserved</summary>
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      {confirmResult.conflicts.slice(0, 20).map((c, i) => (
                        <li key={i}>
                          Row {c.row} · {c.entity} · {c.name}: {c.fields.map((f) => `${f.field}="${f.existing}" (kept, incoming "${f.incoming}")`).join("; ")}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {confirmResult.errors && confirmResult.errors.length > 0 && (
                  <ul className="mx-auto mt-3 max-w-md space-y-1 text-left text-xs text-brand-red">
                    {confirmResult.errors.slice(0, 5).map((e, i) => <li key={i}>• Row {e.row}: {e.reason}</li>)}
                  </ul>
                )}
              </>
            ) : (
              <>
                <h3 className="mt-3 font-display text-lg font-semibold tracking-tight">Imported {confirmResult.imported} · Updated {confirmResult.updated}</h3>
                {confirmResult.skipped > 0 && <p className="mt-1 text-sm text-muted-foreground">Skipped {confirmResult.skipped} rows.</p>}
                {confirmResult.errors && confirmResult.errors.length > 0 && (
                  <ul className="mx-auto mt-3 max-w-md space-y-1 text-left text-xs text-muted-foreground">
                    {confirmResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                )}
              </>
            )}
            <button className={`${primary} mt-4`} onClick={close} data-testid="csv-wizard-done-close">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ current }) {
  const steps = [
    ["upload", "Upload"], ["preview", "Preview"], ["map", "Map"],
    ["validate", "Validate"], ["done", "Done"],
  ];
  const idx = steps.findIndex((s) => s[0] === current);
  return (
    <div className="flex items-center gap-2 overflow-x-auto text-[11px]" data-testid="csv-wizard-stepper">
      {steps.map(([k, l], i) => (
        <div key={k} className="flex items-center gap-2">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${i <= idx ? "brand-gradient-bg text-white" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
          <span className={`${i === idx ? "font-semibold text-foreground" : "text-muted-foreground"} whitespace-nowrap`}>{l}</span>
          {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = tone === "ok" ? "border-brand-teal/40 bg-brand-teal/5 text-brand-teal" :
                  tone === "warn" ? "border-amber-500/40 bg-amber-500/10 text-amber-600" :
                  "border-blue-500/40 bg-blue-500/10 text-blue-600";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="mt-0.5 font-display text-xl font-semibold">{value}</p>
    </div>
  );
}

export function CsvControlsV2({ kind, onImported }) {
  const [open, setOpen] = useState(false);
  const [relOpen, setRelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const exportCsv = async () => {
    try {
      setBusy(true);
      const { data } = await api.get(`/csv/export/${kind}`, { responseType: "text" });
      const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="csv-controls">
      <button onClick={() => setRelOpen(true)} disabled={busy} data-testid="csv-import-crm-btn" className={btn}>
        <Database className="h-3.5 w-3.5" /> Import CRM data
      </button>
      <button onClick={() => setOpen(true)} disabled={busy} data-testid="csv-import-btn" className={btn}>
        <Upload className="h-3.5 w-3.5" /> Import {kind}
      </button>
      <button onClick={exportCsv} disabled={busy} data-testid="csv-export-btn" className={btn}>
        <Download className="h-3.5 w-3.5" /> Export
      </button>
      <CsvImportWizard kind={kind} open={open} onOpenChange={setOpen} onImported={onImported} />
      <CsvImportWizard kind="relational" open={relOpen} onOpenChange={setRelOpen} onImported={onImported} />
    </div>
  );
}

function ConflictPicker({ label, value, onChange, testid }) {
  const options = [
    ["fill_blanks", "Fill blanks"],
    ["keep_existing", "Keep existing"],
    ["overwrite", "Overwrite"],
  ];
  return (
    <div data-testid={testid}>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([k, l]) => (
          <button
            key={k}
            onClick={() => onChange(k)}
            data-testid={`${testid}-${k}`}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${value === k ? "border-brand-teal bg-brand-teal/10 text-brand-teal" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function RelStat({ label, created, reused, updated, skipped, testid }) {
  return (
    <div className="rounded-xl border border-brand-teal/40 bg-brand-teal/5 p-3" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-teal">{label}</p>
      <p className="mt-1 font-display text-xl font-semibold">{created}</p>
      <p className="text-[10px] text-muted-foreground">created</p>
      {(reused > 0 || updated > 0 || skipped > 0) && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          {reused > 0 && <>{reused} reused</>}
          {updated > 0 && <> · {updated} updated</>}
          {skipped > 0 && <> · {skipped} skipped</>}
        </p>
      )}
    </div>
  );
}
